import { UserAuditLogEntry } from '@/apis/core/UserAuditLog/post/postAuditLog';
import { UserWorkspace } from '@/apis/core/workspace/get/getUserWorkspace';

export type RecentActivityActionFilter =
  | 'all'
  | 'Created'
  | 'Opened'
  | 'Modified'
  | 'Deleted'
  | 'Added'
  | 'Removed';

export type RecentActivityEntityFilter =
  | 'all'
  | 'Workspace'
  | 'AskSession'
  | 'DataRoomFile'
  | 'DataRoomCollection'
  | 'Workflow';

/** Entity types shown in compact recent activity (noise excluded). */
export const RECENT_ACTIVITY_ENTITY_TYPES = new Set<string>([
  'Workspace',
  'AskSession',
  'DataRoomFile',
  'DataRoomCollection',
  'CollectionDataRoomFile',
  'Workflow',
  'ChatPrompt',
  'ChatSession',
]);

const EXCLUDED_ENTITY_TYPES = new Set(['AskMessage', 'WorkspaceUser']);

export const RECENT_ACTIVITY_ACTION_OPTIONS: RecentActivityActionFilter[] = [
  'all',
  'Created',
  'Opened',
  'Modified',
  'Deleted',
  'Added',
  'Removed',
];

export const RECENT_ACTIVITY_ENTITY_OPTIONS: RecentActivityEntityFilter[] = [
  'all',
  'Workspace',
  'AskSession',
  'DataRoomFile',
  'DataRoomCollection',
  'Workflow',
];

export type RecentActivityDisplayItem = {
  id: string;
  title: string;
  lastUsed: string;
  recentActivityAction: string;
  recentActivityEntityType: string;
  recentActivityTimestamp: string;
  metaLine: string;
  route: string | null;
};

export const parseAuditResponse = (
  response: unknown,
): { userId: string; logs: UserAuditLogEntry[] }[] => {
  if (Array.isArray(response)) {
    return response as { userId: string; logs: UserAuditLogEntry[] }[];
  }
  if (response && typeof response === 'object' && 'logs' in response) {
    return [response as { userId: string; logs: UserAuditLogEntry[] }];
  }
  return [];
};

const dedupeKey = (log: UserAuditLogEntry) =>
  `${log.entityType}|${log.entityId}|${log.action}`;

/** Keep the latest log per entity + action (logs should be sorted newest first). */
export const dedupeAuditLogs = (logs: UserAuditLogEntry[]): UserAuditLogEntry[] => {
  const seen = new Set<string>();
  const result: UserAuditLogEntry[] = [];
  for (const log of logs) {
    const key = dedupeKey(log);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(log);
  }
  return result;
};

export const filterAuditLogs = (
  logs: UserAuditLogEntry[],
  actionFilter: RecentActivityActionFilter,
  entityFilter: RecentActivityEntityFilter,
): UserAuditLogEntry[] => {
  return logs.filter((log) => {
    if (EXCLUDED_ENTITY_TYPES.has(log.entityType)) return false;
    if (!RECENT_ACTIVITY_ENTITY_TYPES.has(log.entityType)) return false;
    if (actionFilter !== 'all' && log.action !== actionFilter) return false;
    if (entityFilter !== 'all' && log.entityType !== entityFilter) {
      if (
        entityFilter === 'DataRoomFile' &&
        log.entityType === 'CollectionDataRoomFile'
      ) {
        return true;
      }
      return false;
    }
    return true;
  });
};

export const formatEntityTypeLabel = (entityType: string): string => {
  const labels: Record<string, string> = {
    Workspace: 'workspace',
    AskSession: 'ask session',
    AskMessage: 'message',
    DataRoomFile: 'document',
    DataRoomCollection: 'collection',
    CollectionDataRoomFile: 'document',
    Workflow: 'workflow',
    ChatPrompt: 'prompt',
    ChatSession: 'chat session',
  };
  return labels[entityType] ?? entityType.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
};

export const formatActivityMetaLine = (log: UserAuditLogEntry): string => {
  const entityLabel = formatEntityTypeLabel(log.entityType);
  return `${log.action} ${entityLabel}`;
};

const findWorkspaceForAskSession = (
  sessionId: string,
  workspaces: UserWorkspace[],
): UserWorkspace | undefined =>
  workspaces.find((ws) =>
    ws.askSessions?.some(
      (s) => s.id === sessionId || s.askSessionId === sessionId,
    ),
  );

export const buildActivityRoute = (
  log: UserAuditLogEntry,
  workspaces: UserWorkspace[],
): string | null => {
  const { entityType, entityId } = log;
  if (!entityId) return null;

  if (entityType === 'Workspace') {
    return `/workspace/${entityId}`;
  }

  if (entityType === 'AskSession') {
    const ws = findWorkspaceForAskSession(entityId, workspaces);
    return ws ? `/workspace/${ws.id}/ask-session/${entityId}` : null;
  }

  if (entityType === 'DataRoomFile' || entityType === 'CollectionDataRoomFile') {
    for (const ws of workspaces) {
      const files = ws.dataRoom?.workspaceDataRoomFiles ?? [];
      const hasFile = (files as { dataRoomFileId?: string; id?: string }[]).some(
        (f) => f.dataRoomFileId === entityId || f.id === entityId,
      );
      if (hasFile) {
        return `/workspace/${ws.id}/documents?fileId=${entityId}`;
      }
    }
    const fallback = workspaces[0];
    return fallback ? `/workspace/${fallback.id}/documents` : null;
  }

  if (entityType === 'DataRoomCollection') {
    const ws = workspaces[0];
    return ws ? `/workspace/${ws.id}/documents` : null;
  }

  if (entityType === 'Workflow') {
    const ws = workspaces.find((w) =>
      (w.workflows as { id?: string; workflowId?: string }[] | undefined)?.some(
        (wf) => (wf.id ?? wf.workflowId) === entityId,
      ),
    );
    return ws ? `/workspace/${ws.id}/documents` : null;
  }

  return null;
};

export const mapAuditLogsToDisplayItems = (
  logs: UserAuditLogEntry[],
  workspaces: UserWorkspace[],
): RecentActivityDisplayItem[] =>
  logs.map((log, index) => {
    const timestamp = new Date(log.date).toLocaleString();
    const title = log.entityName?.trim() || formatEntityTypeLabel(log.entityType);
    return {
      id: `${log.date}-${log.entityId}-${log.action}-${index}`,
      title,
      lastUsed: timestamp,
      recentActivityAction: log.action,
      recentActivityEntityType: log.entityType,
      recentActivityTimestamp: timestamp,
      metaLine: formatActivityMetaLine(log),
      route: buildActivityRoute(log, workspaces),
    };
  });

export const processAuditLogsForRecentActivity = (
  rawLogs: UserAuditLogEntry[],
  actionFilter: RecentActivityActionFilter,
  entityFilter: RecentActivityEntityFilter,
): UserAuditLogEntry[] => {
  const sorted = [...rawLogs].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
  const filtered = filterAuditLogs(sorted, actionFilter, entityFilter);
  return dedupeAuditLogs(filtered);
};
